/**
 * @format
 * @license SPDX-License-Identifier: Apache-2.0
 */

import { useEffect, useRef, useState } from "react";
import { motion, useInView, useReducedMotion } from "motion/react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import DotGrid from "../DotGrid";
import { useGsapScrollParallax } from "@/hooks/useGsapScrollParallax";
interface StatItem {
	value: number;
	labelKey: string;
}

const mainStat: StatItem = {
	value: 150000,
	labelKey: "stats.items.unitsDelivered",
};

const stats: StatItem[] = [
	{
		value: 150000,
		labelKey: "stats.items.unitsDelivered",
	},
	{
		value: 1050,
		labelKey: "stats.items.activeClients",
	},
	{
		value: 10000,
		labelKey: "stats.items.facility",
	},
	{
		value: 24,
		labelKey: "stats.items.countriesServed",
	},

	{
		value: 25,
		labelKey: "stats.items.yearsExperience",
	},

	{
		value: 15,
		labelKey: "stats.items.globalCertifications",
	},
];

const useCounter = (end: number, duration = 2) => {
	const [count, setCount] = useState(0);
	const ref = useRef(null);
	const isInView = useInView(ref, { once: true, margin: "-120px" });

	useEffect(() => {
		if (!isInView) {
			return;
		}

		let start = 0;
		const increment = end / (duration * 60);
		const timer = setInterval(() => {
			start += increment;
			if (start >= end) {
				setCount(end);
				clearInterval(timer);
				return;
			}
			setCount(Math.floor(start));
		}, 16.67);

		return () => clearInterval(timer);
	}, [isInView, end, duration]);

	return { count, ref };
};

const StatCard = ({ stat, index }: { stat: StatItem; index: number }) => {
	const { t } = useTranslation();
	const { count, ref } = useCounter(stat.value);
	const shouldReduceMotion = useReducedMotion();

	return (
		<motion.div
			ref={ref}
			initial={{ opacity: 0, y: shouldReduceMotion ? 0 : 24 }}
			whileInView={{ opacity: 1, y: 0 }}
			transition={{
				duration: 0.55,
				delay: index * 0.12,
				ease: [0.22, 1, 0.36, 1],
			}}
			viewport={{ once: true, amount: 0.35 }}
			whileHover={shouldReduceMotion ? {} : { y: -6, scale: 1.01 }}
			className={`  premium-card self-center  text-center group p-4 sm:p-5 md:p-6 lg:p-7`}>
			<div className="absolute inset-0 z-0">
				<DotGrid
					dotSize={4}
					gap={15}
					baseColor="#e2ebf5"
					activeColor="#f09a38"
					proximity={100}
					shockRadius={200}
					shockStrength={3}
					resistance={750}
					returnDuration={1.5}
				/>
			</div>
			<div className="relative z-10">
				<p className="text-2xl xs:text-3xl z-99 sm:text-4xl md:text-5xl lg:text-5xl font-semibold tracking-tight text-foreground">
					{count.toLocaleString()}+
				</p>
				<p className="mt-2 text-[10px]  z-99  xs:text-xs sm:text-sm font-semibold uppercase tracking-[0.2em] text-muted-foreground">
					{t(stat.labelKey)}
				</p>
			</div>
		</motion.div>
	);
};

const MainStatCard = () => {
	const { t } = useTranslation();
	const { count, ref } = useCounter(mainStat.value);
	const shouldReduceMotion = useReducedMotion();

	return (
		<motion.div
			initial={{ opacity: 0, y: shouldReduceMotion ? 0 : 18 }}
			whileInView={{ opacity: 1, y: 0 }}
			transition={{
				duration: 0.55,
				delay: 0.1,
				ease: [0.22, 1, 0.36, 1],
			}}
			viewport={{ once: true, amount: 0.35 }}
			className="premium-card relative overflow-hidden h-full min-h-[200px] sm:min-h-[250px] md:min-h-[280px]  rounded-xl">
			<div className="absolute z-99 inset-0 bg-gradient-to-t from-black/80 via-black/30 to-black/20  rounded-xl" />

			<img
				className="h-full w-full object-cover rounded-2xl hover:scale-102 transition-transform duration-300"
				src="/images/Edited.jpg"
				alt={t("common.ui.industrialFacility")}
			/>

			<Link
				to="/our-story"
				className="absolute z-999 bottom-3 sm:bottom-5 left-3 sm:left-5 rounded-full border border-border bg-background/75 px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium text-foreground backdrop-blur hover:bg-background transition-colors">
				{t("stats.knowMore")}
			</Link>
		</motion.div>
	);
};

export default function StatsComponent() {
	const { t } = useTranslation();
	const shouldReduceMotion = useReducedMotion();
	const boxRef = useRef<HTMLDivElement | null>(null);
	useGsapScrollParallax(boxRef);

	return (
		<section
			ref={boxRef}
			className="site-section bg-background">
			<div className="pointer-events-none absolute inset-0 opacity-50">
				<div className="absolute -left-20 top-4 h-72 w-72 rounded-full bg-accent-soft blur-3xl" />
				<div className="absolute right-0 top-1/3 h-80 w-80 rounded-full bg-primary/10 blur-3xl" />
			</div>

			<div className="site-container px-4 sm:px-6 md:px-8 lg:px-12 mx-auto max-w-7xl w-full relative z-10">
				<motion.div
					initial={{ opacity: 0, y: shouldReduceMotion ? 0 : 18 }}
					whileInView={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
					viewport={{ once: true, amount: 0.4 }}
					className="mb-8 sm:mb-10 md:mb-12 lg:mb-14">
					<span className="section-kicker text-xs sm:text-sm">
						<span className="section-kicker-line" />
						{t("stats.kicker")}
					</span>
					<h2 className="section-title text-2xl xs:text-3xl sm:text-4xl md:text-5xl lg:text-6xl max-w-5xl mt-3 sm:mt-4">
						{t("stats.title")}
					</h2>
				</motion.div>

				<div className="grid gap-6 md:gap-8 lg:grid-cols-[1.1fr_0.95fr] lg:items-stretch">
					<MainStatCard />

					<div className="grid gap-2 grid-cols-2 sm:grid-cols-2">
						{stats.map((stat, index) => (
							<StatCard
								key={stat.labelKey}
								stat={stat}
								index={index}
							/>
						))}
					</div>
				</div>
			</div>
		</section>
	);
}
